<template>
  <div>
    <label class="label" name="method">Method</label>
    <v-select
      v-model="selectedMethodIds"
      :options="methods"
      :reduce="item => item._id"
      label="name"
      placeholder="Select Methods"
      class="w-full"
      :disabled="methods.length === 0 || isProcessing"
      :clearable="true"
      :multiple="true"
      :closeOnSelect="false"
      @input="handleMethodChange"
    />
    <p v-if="methods.length === 0" class="text-xs text-warning mb-3">
      Method options are empty.
    </p>
    <p v-else-if="methods.length > 0 && localMethodId.length === 0" class="text-xs text-warning mb-3">
      No methods selected.
    </p>
    <vs-button
      type="line"
      class="p-0 pb-1 text-xs text-left"
      @click="methodPopupHandler"
    >
      Manage Methods
    </vs-button>
        <vs-popup class="method-popup" title="Manage Methods" :active.sync="methodPopupActive">
          <div class="p-8 flex flex-col gap-5">
            <div>
              <h3>Manage Methods</h3>
              <span class="text-base pt-3">Add, edit, or remove method options</span>
            </div>
            <div class="flex flex-col lg:grid lg:grid-cols-3 gap-4">
              <div class="col-span-1 flex flex-col gap-2">
                <vs-input
                  label="Method Name"
                  class="w-full"
                  placeholder="e.g. Injection, Topical, etc."
                  v-model="currentMethod.name"
                  :success="!!(!errors.first('method.methodtxtbox') && currentMethod.name)"
                  val-icon-success="done"
                  val-icon-danger="error"
                  :danger="errors.first('method.methodtxtbox') ? true : false"
                  v-validate="methodPopupActive ? 'required' : ''"
                  name="methodtxtbox"
                  ref="methodtxtbox"
                  :danger-text="errors.first('method.methodtxtbox')"
                  data-vv-as="method name"
                  data-vv-scope="method"
                  :disabled="isProcessing"
                />
                <vs-button @click="addEditMethodOption" class="mt-2" :disabled="isProcessing">
                  {{ isEditMethod ? 'Update' : 'Add' }} Option
                </vs-button>
                <vs-button v-if="isEditMethod" @click="resetForm()" type="border" :disabled="isProcessing">Cancel</vs-button>
              </div>
              <vs-divider class="lg:hidden"></vs-divider>
              <div class="lg:col-span-2 lg:border lg:border-t-0 lg:border-r-0 lg:border-b-0 lg:border-solid d-theme-border-grey-light lg:pl-4">
                <h5 class="mb-3">Existing Method Options</h5>
                <div v-if="methods.length > 0" class="max-h-xs overflow-y-auto">
                  <div
                    v-for="method in methods.filter(r => r._id !== 'other')"
                    :key="method._id"
                  >
                    <div class="flex justify-between items-center">
                      <span>
                        {{ method.name }}
                      </span>
                      <div class="flex justify-center">
                        <div
                          v-if="currentlyDeletingMethodId != method._id"
                          class="flex gap-2"
                        >
                          <vs-button
                            @click="editMethodHandler(method)"
                            icon-pack="feather"
                            icon="icon-edit"
                            type="flat"
                            :disabled="isProcessing"
                          >
                          </vs-button>
                          <vs-button
                            @click="deleteMethodHandler(method)"
                            icon-pack="feather"
                            icon="icon-trash-2"
                            type="flat"
                            :disabled="isProcessing"
                          >
                          </vs-button>
                        </div>
                        <div
                          v-if="currentlyDeletingMethodId === method._id"
                          class="flex gap-2"
                          :key="'edit-' + method._id"
                        >
                          <vs-button
                            @click="confirmDeleteMethod(method)"
                            icon-pack="feather"
                            icon="icon-check"
                            type="flat"
                            color="danger"
                            :disabled="isProcessing"
                          >
                          </vs-button>
                          <vs-button
                            @click="currentlyDeletingMethodId = ''"
                            icon-pack="feather"
                            icon="icon-x"
                            type="flat"
                            :disabled="isProcessing"
                          >
                          </vs-button>
                        </div>
                      </div>
                    </div>
                    <vs-divider class="my-1"></vs-divider>
                  </div>
                </div>
                <div v-else class="flex items-center justify-center h-32">
                  <p>No Method options.</p>
                </div>
              </div>
            </div>
          </div>
        </vs-popup>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import vSelect from 'vue-select';

export default {
  name: 'MethodManager',
  components: {
    vSelect,
  },
  props: {
    value: {
      type: [String, Array],
      default: () => [],
    },
  },
  data() {
    return {
      localMethodId: [],
      methods: [],
      methodPopupActive: false,
      currentMethod: {
        name: '',
        id: '',
      },
      isEditMethod: false,
      currentlyDeletingMethodId: '',
      isProcessing: false,
      initialized: false,
    };
  },
  computed: {
    selectedMethodIds: {
      get() {
        return this.localMethodId;
      },
      set(next) {
        const ids = this.normalizeIds(next);
        const currentIds = JSON.stringify(this.localMethodId.slice().sort());
        const newIds = JSON.stringify(ids.slice().sort());

        // Only update if actually different
        if (currentIds !== newIds) {
          this.localMethodId = ids;
          // Emit the input event for v-model binding
          this.$emit('input', ids);
        }
      },
    },
  },
  watch: {
    value: {
      handler(newVal) {
        // Only sync from parent if initialized and not processing
        if (this.initialized && !this.isProcessing) {
          const normalized = this.normalizeIds(newVal);
          const validIds = normalized.filter(id =>
            this.methods.some(method => method._id === id)
          );
          
          const currentIds = JSON.stringify(this.localMethodId.slice().sort());
          const newIds = JSON.stringify(validIds.slice().sort());
          
          if (currentIds !== newIds) {
            this.localMethodId = validIds;
          }
        }
      },
      deep: true,
      immediate: false,
    },
    methodPopupActive(newVal) {
      // Reset state when modal closes
      if (!newVal) {
        this.currentlyDeletingMethodId = '';
        this.isEditMethod = false;
        this.isProcessing = false;
        // Sync with parent when modal closes
        this.syncWithParent();
      }
    },
  },
  methods: {
    ...mapActions('method', [
      'fetchMethods',
      'createMethod',
      'updateMethod',
      'deleteMethod',
    ]),
    
    // Normalize incoming values (array of ids or objects) to array of ids
    normalizeIds(input) {
      if (!input) return [];
      const arrayValue = Array.isArray(input) ? input : [input];
      return arrayValue
        .map(v => {
          if (v && typeof v === 'object') {
            // Support {_id}, {id}
            return v._id || v.id || null;
          }
          return v;
        })
        .filter(Boolean);
    },

    handleMethodChange(value) {
      // Delegate to computed setter for consistency
      this.selectedMethodIds = value;
    },

    syncWithParent() {
      // Sync localMethodId with parent value, filtering out invalid IDs
      const normalized = this.normalizeIds(this.value);
      const validIds = normalized.filter(id =>
        this.methods.some(method => method._id === id)
      );
      
      const currentIds = JSON.stringify(this.localMethodId.slice().sort());
      const newIds = JSON.stringify(validIds.slice().sort());
      
      if (currentIds !== newIds) {
        this.localMethodId = validIds;
        this.$emit('input', validIds);
      }
    },

    async getMethods() {
      try {
        const { data } = await this.fetchMethods();
        if (data.data && data.data.length) {
          this.methods = data.data.map(({_id, name, createdAt, updatedAt}) => ({
            _id,
            name,
            createdAt,
            updatedAt,
          }));
        } else {
          this.methods = [];
        }
      } catch (error) {
        console.error('Error fetching methods:', error);

        this.$vs.notify({
          title: 'Error',
          text: 'Failed to fetch methods',
          color: 'danger',
        });
      }
    },

    methodPopupHandler() {
      this.methodPopupActive = true;
      this.getMethods();
    },

    async addEditMethodOption() {
      if (this.currentMethod.name && this.currentMethod.name.trim() !== '') {
        this.isProcessing = true;

        try {
          if (!this.isEditMethod) {
            await this.createMethod({
              name: this.currentMethod.name,
            });
            this.$vs.notify({
              title: 'Success',
              text: 'Method added successfully.',
              color: 'success',
            });
          } else {
            await this.updateMethod({
              name: this.currentMethod.name,
              _id: this.currentMethod.id,
            });
            this.$vs.notify({
              title: 'Success',
              text: 'Method updated successfully.',
              color: 'success',
            });
          }
          
          await this.getMethods();
          this.resetForm();
        } catch (err) {
          this.$vs.notify({
            title: 'Failed',
            text: `Failed to ${this.isEditMethod ? 'update' : 'add'} method. Try again!`,
            color: 'danger',
          });
        } finally {
          this.isProcessing = false;
        }
      } else {
        this.errors.add({
          field: 'methodtxtbox',
          msg: 'Method name is required',
          scope: 'method',
        });
      }
    },

    async editMethodHandler(method) {
      this.isEditMethod = true;
      this.currentMethod.name = method.name;
      this.currentMethod.id = method._id;
      this.currentlyDeletingMethodId = '';
      
      this.$nextTick(() => {
        const inputRef = this.$refs.methodtxtbox;
        if (inputRef && inputRef.$el) {
          const inputElement = inputRef.$el.querySelector('input');
          if (inputElement) {
            inputElement.focus();
          }
        }
      });
    },

    deleteMethodHandler(method) {
      this.currentlyDeletingMethodId = method._id;
      this.isEditMethod = false;
      this.currentMethod.name = '';
      this.currentMethod.id = '';
      this.$validator.reset();
    },

    async confirmDeleteMethod(method) {
      const deletedId = method._id;

      if(this.isProcessing) return;
      
      this.isProcessing = true;
      this.currentlyDeletingMethodId = '';

      try {
        // Call delete API
        await this.deleteMethod({ _id: deletedId });

        // Refresh from server
        await this.getMethods();

        // Update local selection - filter out deleted ID
        const validMethodIds = this.methods.map(m => m._id);
        this.localMethodId = this.localMethodId.filter(id => validMethodIds.includes(id));

        // Reset form
        this.resetForm();

        this.$vs.notify({
          title: 'Success',
          text: 'Method deleted successfully.',
          color: 'success',
        });

      } catch (err) {
        console.error('Error deleting method:', err);
        
        // Restore state on error
        await this.getMethods();
        this.syncWithParent();
        
        this.$vs.notify({
          title: 'Failed',
          text: 'Failed to delete method. Try again!',
          color: 'danger',
        });
      } finally {
        this.isProcessing = false;
        this.currentlyDeletingMethodId = '';
      }
    },

    resetForm() {
      this.isEditMethod = false;
      this.currentMethod.name = '';
      this.currentMethod.id = '';
      this.currentlyDeletingMethodId = '';
      this.$validator.reset();
    },
  },

  async mounted() {
    this.isProcessing = false;

    await this.getMethods();

    // Initialize localMethodId from prop value
    const normalized = this.normalizeIds(this.value);
    const validIds = normalized.filter(id =>
      this.methods.some(method => method._id === id)
    );
    this.localMethodId = validIds;

    this.initialized = true;
  },
};
</script>

<style scoped>
.method-popup {
  z-index: 9999;
}

/* Custom styling for v-select text */
.v-select >>> .vs__dropdown-toggle {
  font-size: 14px;
}

.v-select >>> .vs__selected {
  font-size: 14px;
}

.v-select >>> .vs__dropdown-option {
  font-size: 14px;
}

.v-select >>> .vs__dropdown-option--highlight {
  font-size: 14px;
}
</style>
